import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';

export interface MatchRespondDetails {
  request_id: string;
  status: string;
  expire_at: string;
  now: string;
  search_filters: Record<string, unknown>;
  student: { display_name: string; first_name?: string; nickname?: string };
  my_status: 'pending' | 'confirmed' | 'denied';
  my_responded_at: string | null;
  my_deny_reason: string | null;
}

export interface MatchRespondError {
  error: string;
  reason?: string;
  detail?: unknown;
}

async function invokeRespond<T>(body: Record<string, unknown>): Promise<T> {
  // Route the teacher-matching flow (get/confirm/deny) through the new
  // edge function that also pushes a Telegram notification on confirm.
  // The legacy `teacher-match-respond` is intentionally kept untouched
  // because other parts of the system may still call it directly.
  const { data, error } = await supabase.functions.invoke('teacher_matching_approved_telegram_notify', { body });
  if (error) {
    let parsed: any = null;
    try {
      const ctx = (error as any).context;
      if (ctx && typeof ctx.json === 'function') parsed = await ctx.json();
    } catch { /* ignore */ }
    const err: MatchRespondError = parsed || { error: error.message };
    throw err;
  }
  return data as T;
}

export function useMatchRespondDetails(requestId: string | undefined) {
  return useQuery<MatchRespondDetails, MatchRespondError>({
    queryKey: ['teacher-match-respond', requestId],
    enabled: !!requestId,
    queryFn: () => invokeRespond<MatchRespondDetails>({ action: 'get', request_id: requestId }),
    retry: false,
    refetchOnWindowFocus: false,
  });
}

export function useMatchRespondMutation(requestId: string | undefined) {
  const qc = useQueryClient();
  return useMutation<{ ok: true; my_status: string }, MatchRespondError, { decision: 'confirm' | 'deny'; deny_reason?: string }>({
    mutationFn: (vars) => invokeRespond({
      action: 'respond',
      request_id: requestId,
      decision: vars.decision,
      deny_reason: vars.deny_reason ?? null,
    }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['teacher-match-respond', requestId] });
    },
  });
}